import { useHelpers } from "@remirror/react";
import { Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useResizeDetector } from "react-resize-detector";
import { useDebouncedValue } from "../../../../../../../common/helperfunctions/useDebouncedValue";
import { SourceInformation } from "../../../../../../../Dataset/tiles/SourceInformation/SourceInformation";
import { Dataset, DatasetFieldLabels, datasetsConstants } from "../../../../../../../api/Datasets";
import { LoadingWrapper } from "../../../../../../../common/LoadingWrapper";
import { useIntersectionObserverWithState } from "../../../../../../../common/helperfunctions/useIntersectionObserver";
import { Alert } from "../../../../../../../common/overlays/Alert/Alert";
import { DatasetAdditionalSettings, EntityAttributes } from "../../entity-types";
import { attachmentsConstants } from "../../../../../../../api/Attachments";
import { Table } from "../../../../../../../common/panels/Detail/DetailTable";
import { DatasetsDetailTable } from "../../../../../../../Dataset/DatasetsDetailTable";
import { compareAndUpdateAttributes } from "../../EntityViews";
import { GenericEntityConstantsEntities } from "../../../../../../../api/GenericConstants";
import { AttachmentsDetailTable } from "../../../../../../../Attachments/AttachmentsDetail";
import { Viewer } from "../../../../../../../ViewerLayout/FullViewer";
import { useQueryClient } from "@tanstack/react-query";
import { SessionContext } from "../../../../../../../common/contexts/SessionContext";

export const DatasetWrapper = ({
  entity,
  attrs,
  getPosition,
  updateAttributes,
  onDoubleClick,
  ignoreIntersectionObserver = false,
  setSubContentIsLoading,
}: {
  entity: Dataset;
  attrs: EntityAttributes<DatasetAdditionalSettings>;
  getPosition?: any;
  updateAttributes: (attrs: EntityAttributes<DatasetAdditionalSettings>) => void;
  onDoubleClick?: (
    e: React.MouseEvent<HTMLDivElement, MouseEvent>,
    dataset: Dataset,
    entityTypeId: GenericEntityConstantsEntities
  ) => void;
  ignoreIntersectionObserver?: boolean;
  setSubContentIsLoading: Dispatch<SetStateAction<boolean>>;
}) => {
  // const [width] = useState<number | undefined>(attrs.additionalSettings?.width);
  const [height, setHeight] = useState<number | undefined>(attrs.additionalSettings?.height ?? 300);
  const { ref, width: datasetContainerWidth, height: datasetContainerHeight } = useResizeDetector();
  const debouncedWidth = useDebouncedValue(datasetContainerWidth, 300);
  const debouncedHeight = useDebouncedValue(datasetContainerHeight, 300);

  const { isViewEditable } = useHelpers();
  const editable = useMemo(() => {
    return isViewEditable();
  }, [isViewEditable]);

  const queryClient = useQueryClient();

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (entity?.parsingState === "NotYetParsed") {
      interval = setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: [datasetsConstants.resource, "detail", entity.id] });
      }, 3000);
    }

    return () => {
      clearTimeout(interval);
    };
  }, [entity.id, entity?.parsingState, queryClient]);

  useEffect(() => {
    if (!!debouncedWidth && !!debouncedHeight) {
      let timeout: NodeJS.Timeout;
      timeout = setTimeout(() => {
        setHeight(debouncedHeight);
      }, 300);
      return () => {
        clearTimeout(timeout);
      };
    }
  }, [debouncedWidth, debouncedHeight]);

  useEffect(() => {
    if (height !== attrs.additionalSettings?.height) {
      compareAndUpdateAttributes(
        { ...attrs, additionalSettings: { ...attrs.additionalSettings, height } },
        attrs,
        updateAttributes
      );
    }
  }, [attrs, height, updateAttributes]);

  // Intersection observer
  // const entry = useIntersectionObserver(ref, { freezeOnceVisible: true });
  const { entry, setCurrentNode } = useIntersectionObserverWithState({ freezeOnceVisible: true });
  const refCallback = useCallback(
    (node: Element | null) => {
      setCurrentNode(node);
    },
    [setCurrentNode]
  );
  const isIntersecting = ignoreIntersectionObserver || !!entry?.isIntersecting;

  const [isViewerLoading, setIsViewerLoading] = useState<boolean>(true);
  useEffect(() => {
    setSubContentIsLoading(isViewerLoading || entity.parsingState === "NotYetParsed");
  }, [entity.parsingState, setSubContentIsLoading, isViewerLoading]);

  const viewerIds = useMemo(() => {
    return !!entity?.id ? [entity.id] : [];
  }, [entity?.id]);

  const { isPrintContext } = useContext(SessionContext);

  return (
    <div style={{ width: "100%", breakInside: "avoid" }}>
      {/* Details */}
      {attrs.additionalSettings?.showDetails && (
        <div style={{ display: "flex", flexDirection: "column", padding: "0 5px" }}>
          {attrs.additionalSettings.showDetails && (
            <>
              {!entity.isViewableEntity ? (
                <DatasetsDetailTable
                  entityTypeId="datasets"
                  entity={entity}
                  entityConstants={datasetsConstants}
                  fieldLabels={DatasetFieldLabels}
                  hideHeader={
                    !(
                      (attrs.additionalSettings.showSourceInformation && !entity.isViewableEntity) ||
                      attrs.additionalSettings.showViewer ||
                      (attrs.additionalSettings.showParameters && !entity.isViewableEntity)
                    )
                  }
                />
              ) : (
                <AttachmentsDetailTable
                  entityTypeId="attachments"
                  entity={entity}
                  entityConstants={attachmentsConstants}
                  fieldLabels={DatasetFieldLabels}
                />
              )}
            </>
          )}
        </div>
      )}
      {/* Source information */}
      {attrs.additionalSettings?.showSourceInformation && !entity.isViewableEntity && (
        <div style={{ display: "flex", flexDirection: "column", padding: "0 5px" }}>
          {attrs.additionalSettings.showSourceInformation && (
            <Table noPadding>
              {(attrs.additionalSettings.showDetails ||
                attrs.additionalSettings.showViewer ||
                (attrs.additionalSettings.showParameters && !entity.isViewableEntity)) && (
                <Table.Head>Source information</Table.Head>
              )}
              <Table.Body>
                <SourceInformation dataset={entity} tile={false} />
              </Table.Body>
            </Table>
          )}
        </div>
      )}
      {/* Viewer */}
      {attrs.additionalSettings?.showViewer && (
        <div style={{ display: "flex", flexDirection: "column", padding: "0 5px" }}>
          <Table noPadding>
            {(attrs.additionalSettings.showDetails ||
              (attrs.additionalSettings.showSourceInformation && !entity.isViewableEntity) ||
              (attrs.additionalSettings.showParameters && !entity.isViewableEntity)) && <Table.Head>Viewer</Table.Head>}
            <Table.Body>
              <div style={{ paddingTop: "5px" }} ref={refCallback}>
                <div
                  id="viewer-container-div"
                  ref={ref}
                  data-toggle="tooltip"
                  data-placement="bottom"
                  title={
                    onDoubleClick && (attrs.entityTypeId === "datasets" || attrs.entityTypeId === "attachments")
                      ? "Double click to open in viewer"
                      : `${entity.name} (ID: ${entity.id})`
                  }
                  style={{
                    resize: editable ? "vertical" : "none",
                    // width: settings.width ? settings.width + "px" : undefined,
                    height: `${height ?? 300}px`,
                    width: "100%",
                    cursor: "pointer",
                    overflow: "hidden",
                  }}
                  onDoubleClick={(e) => {
                    onDoubleClick &&
                      (attrs.entityTypeId === "datasets" || attrs.entityTypeId === "attachments") &&
                      onDoubleClick(e, entity, attrs.entityTypeId === "datasets" ? "datasets" : "attachments");
                  }}
                >
                  {isIntersecting ? (
                    <LoadingWrapper
                      status={entity.parsingState === "NotYetParsed" ? "loading" : "success"}
                      fetchStatus={entity.parsingState === "NotYetParsed" ? "fetching" : "idle"}
                      type="skeleton-block"
                    >
                      <Viewer
                        ids={viewerIds}
                        settings={{
                          showViewer: true,
                          interactiveMode: false,
                          showParameter: false,
                          showNavigation: false,
                          showTrackList: false,
                        }}
                        isViewerLoadingCallback={setIsViewerLoading}
                      />
                    </LoadingWrapper>
                  ) : (
                    <LoadingWrapper
                      status={entity.parsingState === "NotYetParsed" ? "loading" : "success"}
                      fetchStatus={entity.parsingState === "NotYetParsed" ? "fetching" : "idle"}
                      type="skeleton-block"
                    >
                      <div style={{ width: "100%", height: "100%" }} />
                    </LoadingWrapper>
                  )}
                </div>
              </div>
            </Table.Body>
          </Table>
        </div>
      )}
      {/* Parameters */}
      {attrs.additionalSettings?.showParameters && !entity.isViewableEntity && (
        <div style={{ display: "flex", flexDirection: "column", padding: "0 5px" }}>
          <Table noPadding>
            {(attrs.additionalSettings.showDetails ||
              attrs.additionalSettings.showViewer ||
              (attrs.additionalSettings.showSourceInformation && !entity.isViewableEntity)) && (
              <Table.Head>Parameters</Table.Head>
            )}
            <Table.Body>
              <div style={{ height: "300px", width: "100%" }}>
                <LoadingWrapper
                  status={entity.parsingState === "NotYetParsed" ? "loading" : "success"}
                  fetchStatus={entity.parsingState === "NotYetParsed" ? "fetching" : "idle"}
                  type="skeleton-pairs"
                >
                  {entity.parsingState === "ParsedSuccessfully" ? (
                    <Viewer
                      key="ViewerInParameterTile"
                      ids={viewerIds}
                      settings={{ showViewer: false, showParameter: true, showTrackList: false }}
                    />
                  ) : (
                    <Alert type="light" message="No parameters available" fit centered />
                  )}
                </LoadingWrapper>
              </div>
            </Table.Body>
          </Table>
        </div>
      )}
      {/* Nothing selected */}
      {!attrs.additionalSettings?.showViewer &&
        !attrs.additionalSettings?.showDetails &&
        !attrs.additionalSettings?.showSourceInformation &&
        !attrs.additionalSettings?.showParameters &&
        !isPrintContext && (
          <Alert
            style={{ margin: 0, width: "100%" }}
            type="light"
            message={
              <div style={{ width: "100%" }}>
                <div>All sections are currently disabled.</div>
                <div style={{ fontWeight: 400, fontSize: "1.1rem" }}>
                  View options are available in the settings of this block (edit mode only).
                </div>
              </div>
            }
            fit
            centered
          />
        )}
    </div>
  );
};
